import React, { useEffect, useRef } from 'react';

import { IS_DISCORD_ACTIVITY } from '@/globals';
import { trackTwitchEngagement } from './twitchUtils';

const TWITCH_EMBED_API_URL = 'https://embed.twitch.tv/embed/v1.js';

// Report watch time this often while playing, so long sessions / tab closes are still captured.
const WATCH_HEARTBEAT_INTERVAL_MS = 2 * 60_000;

interface TwitchPlayerOptions {
    channel: string;
    parent: string[];
    width: string;
    height: string;
    autoplay: boolean;
    muted: boolean;
    allowfullscreen: boolean;
}

interface TwitchPlayerInstance {
    play(): void;
    pause(): void;
    isPaused(): boolean;
    addEventListener(event: string, callback: () => void): void;
    removeEventListener(event: string, callback: () => void): void;
}

interface TwitchPlayerConstructor {
    new (element: HTMLElement, options: TwitchPlayerOptions): TwitchPlayerInstance;
    PLAY: string;
    PAUSE: string;
    ONLINE: string;
    OFFLINE: string;
}

declare global {
    interface Window {
        Twitch?: {
            Player: TwitchPlayerConstructor;
        };
    }
}

function loadTwitchAPI(onReady: () => void) {
    if (window.Twitch) {
        onReady();
        return;
    }

    const existingScript = document.querySelector(`script[src="${TWITCH_EMBED_API_URL}"]`);
    if (existingScript) {
        existingScript.addEventListener('load', onReady);
        return;
    }

    const script = document.createElement('script');
    script.src = TWITCH_EMBED_API_URL;
    script.async = true;
    script.onload = onReady;
    document.head.appendChild(script);
}

interface TwitchPlayerProps {
    channel: string;
    videoUrl: string;
    className?: string;
}

// Self-contained live player, independent of the VOD VideoPlayer.
export function TwitchPlayer({ channel, videoUrl, className }: TwitchPlayerProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isTwitchAPIReady, setIsTwitchAPIReady] = React.useState(false);
    const nativePlayerRef = useRef<TwitchPlayerInstance | null>(null);

    // playStartedAtRef marks the first play; watch time is elapsed since then.
    const playStartedAtRef = useRef<number | null>(null);
    const heartbeatIntervalRef = useRef<number | null>(null);

    useEffect(() => {
        loadTwitchAPI(() => setIsTwitchAPIReady(true));
    }, []);

    useEffect(() => {
        if (!isTwitchAPIReady) return;
        if (!containerRef.current || !window.Twitch || !channel) return;

        // Clear any previous player mount
        containerRef.current.innerHTML = '';

        const parent = IS_DISCORD_ACTIVITY
            ? [window.location.hostname, 'discord.com', 'discordapp.com', 'canary.discord.com', 'ptb.discord.com']
            : [window.location.hostname];

        const player = new window.Twitch.Player(containerRef.current, {
            channel,
            parent,
            width: '100%',
            height: '100%',
            autoplay: true,
            muted: false,
            allowfullscreen: false,
        });

        nativePlayerRef.current = player;

        const { PLAY, PAUSE, ONLINE, OFFLINE } = window.Twitch.Player;

        // Seconds elapsed since the first play.
        const elapsedSeconds = () =>
            playStartedAtRef.current !== null ? (Date.now() - playStartedAtRef.current) / 1000 : 0;

        const startHeartbeat = () => {
            if (heartbeatIntervalRef.current !== null) return;
            heartbeatIntervalRef.current = window.setInterval(() => {
                trackTwitchEngagement('heartbeat', videoUrl, elapsedSeconds());
            }, WATCH_HEARTBEAT_INTERVAL_MS);
        };

        const stopHeartbeat = () => {
            if (heartbeatIntervalRef.current !== null) {
                clearInterval(heartbeatIntervalRef.current);
                heartbeatIntervalRef.current = null;
            }
        };

        const handlePlay = () => {
            if (playStartedAtRef.current === null) playStartedAtRef.current = Date.now();
            startHeartbeat();
            trackTwitchEngagement('play', videoUrl);
        };

        const handlePause = () => trackTwitchEngagement('pause', videoUrl);
        const handleOnline = () => trackTwitchEngagement('online', videoUrl);
        const handleOffline = () => trackTwitchEngagement('offline', videoUrl);

        player.addEventListener(PLAY, handlePlay);
        player.addEventListener(PAUSE, handlePause);
        player.addEventListener(ONLINE, handleOnline);
        player.addEventListener(OFFLINE, handleOffline);

        return () => {
            player.removeEventListener(PLAY, handlePlay);
            player.removeEventListener(PAUSE, handlePause);
            player.removeEventListener(ONLINE, handleOnline);
            player.removeEventListener(OFFLINE, handleOffline);

            // Final watch time on unmount / channel change.
            stopHeartbeat();
            if (playStartedAtRef.current !== null) {
                trackTwitchEngagement('watch_time', videoUrl, elapsedSeconds());
            }
            playStartedAtRef.current = null;
            nativePlayerRef.current = null;
        };
    }, [isTwitchAPIReady, channel]);

    return <div ref={containerRef} className={className} style={{ width: '100%', height: '100%' }} />;
}
